import React from 'react';
import { Typography } from '@mui/material';
import { Li, Ul } from '../components/layout/SimpleLists';

export const kriDescription = (
  <>
    <Typography gutterBottom>
      The KRI Report (Key Risk Indicators) provides period-based AML/KYC operational metrics across
      requests, pings, cases, entities, PEP/sanction outcomes, suspicious reporting, and risk class
      distribution.
    </Typography>
    <Typography gutterBottom>
      You can define one or more periods. For each period, the report calculates metrics from
      {' '}
      <strong>From</strong>
      {' '}
      date (inclusive) to
      {' '}
      <strong>To</strong>
      {' '}
      date (inclusive).
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Track regulatory and operational AML/KYC KPIs over time
      </Li>
      <Li>
        Compare volumes and case outcomes between multiple reporting windows
      </Li>
      <Li>
        Monitor portfolio risk-class and sanctions/PEP alert trends in a single overview
      </Li>
    </Ul>
  </>
);

export const pepSanctionHitsDescription = (
  <>
    <Typography gutterBottom>
      The PEP &amp; Sanction Hits Threshold Variation report analyses match results for individuals
      in the system against PEP lists, sanction lists, and adverse media sources across multiple
      match probability thresholds (70, 75, 80, 85, 90, 95, 100).
    </Typography>
    <Typography gutterBottom>
      For each individual created within the selected date range, the report shows whether a
      matching record exists at each threshold level. This enables compliance teams to assess the
      sensitivity of their screening configuration and calibrate match thresholds to balance
      coverage against false-positive rates.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Evaluate the impact of changing your PEP or sanction match threshold
      </Li>
      <Li>
        Identify individuals with high-probability hits that may require manual review
      </Li>
      <Li>
        Produce regulatory evidence of population-wide screening coverage
      </Li>
    </Ul>
  </>
);

export const portfolioScreeningDescription = (
  <>
    <Typography gutterBottom>
      The Portfolio Screening report provides an entity-level overview of PEP, sanction, and
      adverse media screening results across your portfolio.
    </Typography>
    <Typography gutterBottom>
      It shows the latest screening collection timestamps and highest match percentages for each
      entity, and can optionally include latest execution timestamps for selected AML rules.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Review screening coverage for both individuals and businesses
      </Li>
      <Li>
        Identify entities with high screening match percentages that may require review
      </Li>
      <Li>
        Track rule execution recency for selected origination rule IDs
      </Li>
    </Ul>
  </>
);

export const ruleThresholdVariationDescription = (
  <>
    <Typography gutterBottom>
      The Rule Threshold Variation report backtests how your transaction monitoring rules would
      have performed if their thresholds had been set differently during the selected time period.
    </Typography>
    <Typography gutterBottom>
      For each configured rule, the report estimates the number of pings that would have been
      generated if the rule threshold was multiplied by each of the selected factors. For example,
      a factor of 0.75 represents a 25% lower threshold (more triggers), while 1.25 represents a
      25% higher threshold (fewer triggers).
    </Typography>
    <Typography gutterBottom>
      Rules without at least 10 pings will be discarded from the analysis.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Identify over-triggering rules that could benefit from a higher threshold
      </Li>
      <Li>
        Assess rules with few hits that may need a lower threshold to catch more suspicious activity
      </Li>
      <Li>
        Model the operational impact of threshold changes before applying them in production
      </Li>
      <Li>
        Filter by rule type to focus on specific categories (e.g. volume-based or frequency-based
        rules)
      </Li>
    </Ul>
  </>
);

export const ruleStatisticsDescription = (
  <>
    <Typography gutterBottom>
      The Rule Statistics report provides a statistical overview of each transaction monitoring
      rule&apos;s performance during the selected period. It shows how many transactions were
      evaluated, how many triggered the rule, and what happened to the resulting pings.
    </Typography>
    <Typography gutterBottom>
      The report groups rule versions into origination rule IDs so that performance can be tracked
      continuously even when rule parameters are updated over time.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Monitor rule hit rates and compare performance across rules
      </Li>
      <Li>
        Identify rules with no triggers or unusually high trigger rates
      </Li>
      <Li>
        Review confirmed vs. ignored vs. pending ping ratios per rule
      </Li>
      <Li>
        Support periodic AML model governance reviews
      </Li>
    </Ul>
  </>
);

export const ruleOverviewDescription = (
  <>
    <Typography gutterBottom>
      The Rule Overview report provides a structured summary of all rule versions configured in
      the system within the specified version range. It captures the full parameter set of each
      rule version, enabling auditability and governance of rule changes over time.
    </Typography>
    <Typography gutterBottom>
      The report can be filtered to include only request-triggered rules, ongoing rules, or both,
      and it can be limited to specific rule IDs or origination rule IDs. It also supports
      separating test rule versions from production versions.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Audit the history of rule configurations and parameter changes
      </Li>
      <Li>
        Export current rule settings for review or documentation
      </Li>
      <Li>
        Identify rules with specific parameters (e.g. threshold values, group filters)
      </Li>
      <Li>
        Support model risk management reviews and internal audits
      </Li>
    </Ul>
  </>
);

export const requestOverviewDescription = (
  <>
    <Typography gutterBottom>
      The Request Overview report provides a detailed list of all requests processed by the system
      within the selected time window. A request represents a transaction or event submitted to
      Pingwire by the client system.
    </Typography>
    <Typography gutterBottom>
      Each row corresponds to one request, with full details on the request type, amount, currency,
      entity roles, and the resulting signal IDs from the recommendation engine. Filters can be
      applied on request type, transaction type, currency, amount range, and assigned signals.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Investigate specific transactions or request patterns
      </Li>
      <Li>
        Export transaction data for regulatory investigations
      </Li>
      <Li>
        Monitor transaction volumes and types over time
      </Li>
      <Li>
        Analyse which signals are most frequently returned
      </Li>
    </Ul>
  </>
);

export const extendedTransactionsDescription = (
  <>
    <Typography gutterBottom>
      The Extended Transactions report exports transaction-level data together with linked ping
      and case-handling context for the selected timeframe.
    </Typography>
    <Typography gutterBottom>
      Each row represents one ping linked to a transaction and includes transaction details,
      initiator and recipient attributes, ping metadata, and analyst assignment information.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Export detailed transaction and alert history for investigations
      </Li>
      <Li>
        Analyse transaction activity with related ping handling context
      </Li>
      <Li>
        Support customer-specific reporting needs over a custom timeframe
      </Li>
    </Ul>
  </>
);

export const pingOverviewDescription = (
  <>
    <Typography gutterBottom>
      The Ping Overview report provides a comprehensive list of pings generated by the AML
      monitoring rules during the selected time period. A ping is an alert created when a
      transaction monitoring rule is triggered and requires review by an AML analyst.
    </Typography>
    <Typography gutterBottom>
      The report includes full details for each ping: which rule triggered it, the entity involved,
      its current status, tag assignments, and associated case information. A summary sheet
      aggregates ping counts per rule for quick performance review.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Export ping data for regulatory filings or investigations
      </Li>
      <Li>
        Monitor analyst workload and ping resolution rates
      </Li>
      <Li>
        Analyse monthly alert volumes and distribution by rule
      </Li>
      <Li>
        Identify rules with unusual error ping patterns
      </Li>
    </Ul>
  </>
);

export const caseOverviewDescription = (
  <>
    <Typography gutterBottom>
      The Case Overview report provides a comprehensive list of AML investigation cases within the
      selected time period, along with key metrics such as case status, duration, entity roles,
      rule triggers, and analyst assignments.
    </Typography>
    <Typography gutterBottom>
      The report also includes two summary sheets: a tag-based summary showing case counts and
      resolution times by tag category, and an assignment summary showing workload distribution
      across team members.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Monitor case throughput, resolution times, and analyst workloads
      </Li>
      <Li>
        Identify cases linked to specific entities, rules, or data subjects
      </Li>
      <Li>
        Export case data for regulatory filings or SAR (Suspicious Activity Report) support
      </Li>
      <Li>
        Analyse closure patterns and time-in-status metrics
      </Li>
    </Ul>
  </>
);

export const caseStatusHistoryDescription = (
  <>
    <Typography gutterBottom>
      The Case Status History report lists one row per matching status update, including repeated
      changes on the same case. Filter by update time and the status recorded by the update,
      rather than the current case status.
    </Typography>
    <Typography gutterBottom>
      Update timestamps are in UTC. Member details identify who made each change, while the
      additional case columns describe the case at generation time. Rows are sorted by status
      update date and case ID.
    </Typography>
  </>
);

export const groupDurationDescription = (
  <>
    <Typography gutterBottom>
      The Group Duration report calculates the number of days between two specified group
      membership events (assignment or unassignment) for each entity in the system.
    </Typography>
    <Typography gutterBottom>
      For example, you can measure how long it takes from an entity being assigned to a &quot;New
      Customer&quot; group until they are assigned to an &quot;Onboarded&quot; group. This is
      useful for measuring onboarding durations, risk escalation response times, and remediation
      timelines.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Measure time-to-onboard or time-to-remediate for customer segments
      </Li>
      <Li>
        Monitor SLA compliance for entity lifecycle transitions
      </Li>
      <Li>
        Identify entities stuck in an intermediary group without progressing
      </Li>
      <Li>
        Analyse group transition patterns across the portfolio
      </Li>
    </Ul>
  </>
);

export const entityTransactionsDescription = (
  <>
    <Typography gutterBottom>
      The Entity Transactions report aggregates transaction statistics per entity over a selected
      time range and currency. For each entity, it computes total and daily volumes, frequencies,
      and net flows for incoming, outgoing, and processing transactions.
    </Typography>
    <Typography gutterBottom>
      The report can be filtered to specific entity types, groups, and transaction types, enabling
      targeted analysis of subsets of the customer portfolio.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Identify entities with unusually high or low transaction volumes
      </Li>
      <Li>
        Monitor net cash flows per entity for risk assessment
      </Li>
      <Li>
        Export transaction aggregates for regulatory analysis
      </Li>
      <Li>
        Focus on specific entity types or group memberships
      </Li>
    </Ul>
  </>
);

export const entityOverviewDescription = (
  <>
    <Typography gutterBottom>
      The Entity Overview report provides a snapshot of all entities registered in the system
      within the selected date range, including their risk scores, risk class assignments, group
      memberships, and key identifiers.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Export the full entity registry for a given period
      </Li>
      <Li>
        Monitor the risk class distribution of the customer portfolio
      </Li>
      <Li>
        Identify entities belonging to specific groups or risk classes
      </Li>
      <Li>
        Produce data extracts for regulatory submissions or audits
      </Li>
    </Ul>
  </>
);

export const businessUsageDescription = (
  <>
    <Typography gutterBottom>
      The Business Usage Report analyses how individual customers interact with one or more
      specified businesses (counterparties). For each individual, it shows transaction volumes and
      frequencies to and from each of the selected business entities, along with group memberships,
      risk class assignments, and form submission counts.
    </Typography>
    <Typography gutterBottom>
      Historical form count snapshots can also be included for selected reference dates to support
      trend analysis.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Analyse which customers transact most with a specific business or merchant
      </Li>
      <Li>
        Monitor customer interaction with high-risk or flagged businesses
      </Li>
      <Li>
        Track form submission history for individuals over time
      </Li>
      <Li>
        Combine with risk class filtering to focus on specific risk segments
      </Li>
    </Ul>
  </>
);

export const accountFirstInitiatorDescription = (
  <>
    <Typography gutterBottom>
      The Transacting Accounts report shows how many unique accounts transacted during the selected
      date range, plus period-based breakdowns using country indicators.
    </Typography>
    <Typography gutterBottom>
      <strong>Use this report to:</strong>
    </Typography>
    <Ul>
      <Li>
        Track monthly trends in transacting-account volumes
      </Li>
      <Li>
        Monitor the total number of unique accounts that transacted in the full period
      </Li>
      <Li>
        Optionally include country-level and per-account indicator sheets for deeper analysis
      </Li>
    </Ul>
  </>
);
